import React, {
  useEffect, useState, useRef,
} from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Formik, Form, Field, ErrorMessage,
} from 'formik';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import Footer from '~/pages/Manager/Footer';
import PF2MTable from '~/components/PF2MTable';
import {
  getTeamGroups,
  getTeams,
  getTurnsGroups,
  getTurns,
  getTeamsListing,
  addTeamListing,
  setTeamListingValue,
  resetTeamsListing,
  updateTeamsListing,
  removeTeamListing,
} from '~/store/manager/actions';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MAddButton from '~/components/PF2MAddButton';

const TeamTab = ({ classes }) => {
  const teamGroups = useSelector(state => state.manager.teamGroups);
  const teams = useSelector(state => state.manager.teams);
  const turnsGroups = useSelector(state => state.manager.turnsGroups);
  const turns = useSelector(state => state.manager.turns);
  const teamsListing = useSelector(state => state.manager.teamsListing);
  const isTeamsListingDirty = useSelector(state => state.manager.isTeamsListingDirty);
  const dispatch = useDispatch();
  const [selectedTurnGroup, setSelectedTurnGroup] = useState(0);
  const [selectedTeamGroup, setSelectedTeamGroup] = useState(0);
  const [columns, setColumns] = useState([]);
  const formikRef = useRef();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isValidationAlertOpen, setIsValidationAlertOpen] = useState(false);
  const { t: translate } = useTranslation();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState('');

  useEffect(() => {
    async function fetchData() {
      dispatch(getTeamGroups());
      dispatch(getTurnsGroups());
      dispatch(getTurns());
      dispatch(getTeams());
      dispatch(getTeamsListing(0, 0));
    }
    fetchData();
  }, [dispatch]);

  const warningPopup = () => {
    const message = (
      `${translate('validation:CompleteTheRegistrationMenuTabs')}`
    );
    setSuccessDialogMessage(message);
    setShowSuccessDialog(true);
  };

  useEffect(() => {
    async function fetchData() {
      await dispatch(getTeamsListing(selectedTurnGroup, selectedTeamGroup));
      const filteredTeams = teams.filter(t => t.id_team_group === selectedTeamGroup);
      const selectedTurns = turns
        .filter(t => t.id_turn_group === selectedTurnGroup)
        .map(t => ({
          field: `turn_${t.id}`,
          title: t.name,
          width: '150',
          selectItems: filteredTeams,
        }))
        .concat({
          field: 'turn_0',
          title: translate('common:DayOff'),
          selectItems: filteredTeams,
        });
      setColumns([{ field: 'day', title: translate('common:Days').toLocaleLowerCase() }, ...selectedTurns]);
    }
    if (selectedTurnGroup > 0 && selectedTeamGroup > 0) {
      fetchData();
    }
  }, [selectedTurnGroup, selectedTeamGroup, dispatch, teams, turns, translate]);

  const renderHeader = () => (
    <Formik
      ref={formikRef}
      initialValues={{
        turnGroup: selectedTurnGroup,
        teamGroup: selectedTeamGroup,
        days: '',
      }}
      validate={(values) => {
        const errors = {};
        const {
          turnGroup,
          teamGroup,
          days,
        } = values;

        if (!days) {
          errors.name = translate('validation:RequiredField');
        }

        if (turnGroup <= 0 && days) {
          errors.turnGroup = translate('validation:RequiredField');
        }

        if (teamGroup <= 0 && days) {
          errors.teamGroup = translate('validation:RequiredField');
        }

        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        const { teamGroup, turnGroup, days } = values;
        const newValues = {
          days,
          teamGroup,
          turnGroup,
          turns: turns.filter(t => t.id_turn_group === turnGroup)
            .sort((a, b) => a.id - b.id),
        };
        dispatch(addTeamListing(newValues));
        resetForm();
      }}
    >
      {({
        isSubmitting,
        errors,
        isValid,
      }) => (
        <Form>
          <div style={{ display: 'flex', marginBottom: 15, minHeight: 75 }}>
            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:ShiftGroup')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="turnGroup"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      placeholder={null}
                      onChange={async (e) => {
                        if (isTeamsListingDirty) {
                          setIsAlertOpen(true);
                        } else {
                          form.setFieldValue('turnGroup', e.target.value);
                          setSelectedTurnGroup(e.target.value);
                        }
                      }}
                    >
                      {[{ value: 0, label: translate('common:Select') }]
                        .concat(...turnsGroups
                          .map(e => ({ value: e.id, label: e.name })))}
                    </PF2MSearchSelectOutlined>
                  )}
                />
                <ErrorMessage
                  name="turnGroup"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <FormControl style={{ marginLeft: 15 }}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:TeamGroup')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="teamGroup"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      placeholder={null}
                      onChange={async (e) => {
                        if (isTeamsListingDirty) {
                          setIsAlertOpen(true);
                        } else {
                          form.setFieldValue('teamGroup', e.target.value);
                          setSelectedTeamGroup(e.target.value);
                        }
                      }}
                    >
                      {[{ value: 0, label: translate('common:Select') }]
                        .concat(...teamGroups
                          .map(e => ({ value: e.id, label: e.name })))}
                    </PF2MSearchSelectOutlined>
                  )}
                />
                <ErrorMessage
                  name="teamGroup"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <FormControl className={classes.classGroupName}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:DaysQuantity')}:`}
              </FormLabel>
              <FormGroup>
                <FormGroup>
                  <Field
                    type="number"
                    name="days"
                    min={0}
                    className={classnames('form-control', errors.days ? classes.fieldError : classes.field)}
                    style={{ width: 200 }}
                  />
                </FormGroup>
                <ErrorMessage
                  name="days"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <div style={{ marginTop: 12 }}>
              <PF2MAddButton disabled={isSubmitting || !isValid} />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  const resetPage = async () => {
    if (formikRef) {
      formikRef.current.resetForm({ days: '', teamGroup: 0, turnGroup: 0 });
    }
    setColumns([]);
    setSelectedTeamGroup(0);
    setSelectedTurnGroup(0);
    dispatch(getTeamsListing(0, 0));
  };

  const isTeamListingsInvalid = (listings) => {
    const newListings = listings.filter(r => r.id < 0);
    const allListingTurns = [];
    newListings.forEach((listing) => {
      const turnNames = Object.keys(listing).filter(r => r.startsWith('turn'));
      const curListingTurns = [];
      turnNames.forEach((turnName) => {
        if (listing[turnName] !== 0) curListingTurns.push(listing[turnName]);
      });
      allListingTurns.push(curListingTurns);
    });
    return (allListingTurns.some(r => (new Set(r)).size !== r.length));
  };

  return (
    <div className={classes.tabContainer}>
      {renderHeader()}
      <PF2MTable
        data={teamsListing.filter(t => t.update_status !== 'D')}
        columns={columns}
        updateItem={(row, field, value) => {
          dispatch(setTeamListingValue(row, field, value, selectedTurnGroup, selectedTeamGroup));
        }}
        removeItem={row => dispatch(removeTeamListing(row))}
      />
      <Footer
        isDirty={isTeamsListingDirty}
        discard={async () => {
          dispatch(resetTeamsListing());
          await resetPage();
        }}
        sendData={() => {
          if (isTeamListingsInvalid(teamsListing)) {
            setIsValidationAlertOpen(true);
            return;
          }
          dispatch(updateTeamsListing(
            teamsListing, selectedTurnGroup, selectedTeamGroup, warningPopup(),
          ));
        }}
      />
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:YouHaveUnsavedData')}
        open={isAlertOpen}
        onConfirm={() => setIsAlertOpen(false)}
      />
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:CantHaveMultipleTurnsWithSameTeam')}
        open={isValidationAlertOpen}
        onConfirm={() => setIsValidationAlertOpen(false)}
      />
      <PF2MAlertDialog
        title={translate('common:RegistrationSteps')}
        description={
          <div style={styles.descriptionStyle}>{successDialogMessage}</div>
        }
        open={showSuccessDialog}
        onConfirm={() => {
          setShowSuccessDialog(false);
        }}
        onClose={() => setShowSuccessDialog(false)}
        hasCancel={false}
        badge
      />
    </div>
  );
};

TeamTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(TeamTab);
